import { useMemo, useCallback } from 'react';
import { useSearchModifiers } from 'controllers/SearchController/hooks/useSearchModifiers';
import { FiltersParams } from 'types/params';
import { ModifiersPayload } from 'controllers/SearchController/types';
import { useMilestonesController } from 'controllers/business/Milestones/Milestones.controller';
import { useCurrentSearchState } from 'controllers/SearchController/hooks/useCurrentSearchState';
import { KeyQuality, KeyRootApi } from 'types/generated/graphql';

export const useBpmAndKeys = () => {
    const { addFilter, removeFilter } = useSearchModifiers();
    const { bpmlowerbound, bpmupperbound, keyquality, keyrootnote } =
        useCurrentSearchState();

    const activeBpmFilters = useMemo<string[]>(
        () =>
            [bpmlowerbound || bpmupperbound].filter(
                (filter) => filter !== null,
            ) as string[],
        [bpmlowerbound, bpmupperbound],
    );

    const activeKeyFilters = useMemo<string[]>(
        () =>
            [keyrootnote, keyquality].filter(
                (filter) => filter !== null,
            ) as string[],
        [keyquality, keyrootnote],
    );

    const onBpmClear = useCallback(() => {
        removeFilter([
            [FiltersParams.bpmlowerbound, bpmlowerbound],
            [FiltersParams.bpmupperbound, bpmupperbound],
        ]);
    }, [bpmlowerbound, bpmupperbound, removeFilter]);

    const bpm = useMemo(() => {
        const lowerBound =
            bpmlowerbound !== null ? parseInt(bpmlowerbound, 10) : null;
        const upperBound =
            bpmupperbound !== null ? parseInt(bpmupperbound, 10) : null;

        return [lowerBound, upperBound] as [number, number] | [null, null];
    }, [bpmlowerbound, bpmupperbound]);

    const addBpmFilter = useCallback(
        ([min, max]: [number, number | undefined]) => {
            const minString = min.toString();
            const maxString = max === undefined ? minString : max.toString();

            const filter: ModifiersPayload[] = [
                [FiltersParams.bpmlowerbound, minString],
                [FiltersParams.bpmupperbound, maxString],
            ];

            addFilter(filter);
        },
        [addFilter],
    );

    const onKeysClear = useCallback(() => {
        removeFilter([
            [FiltersParams.keyquality, keyquality],
            [FiltersParams.keyrootnote, keyrootnote],
        ]);
    }, [removeFilter]);

    const toggleKeyRootFilter = useCallback(
        (value: KeyRootApi) => {
            if (value === keyrootnote) {
                removeFilter([[FiltersParams.keyrootnote, keyrootnote]]);
            } else {
                addFilter([[FiltersParams.keyrootnote, value]]);
            }
        },
        [keyrootnote, addFilter, removeFilter],
    );

    const toggleKeyQualityFilter = useCallback(
        (value: KeyQuality) => {
            if (value === keyquality) {
                removeFilter([[FiltersParams.keyquality, keyquality]]);
            } else {
                addFilter([[FiltersParams.keyquality, value]]);
            }
        },
        [keyquality, addFilter, removeFilter],
    );

    const { get, perform } = useMilestonesController();
    const isCreatorBannerVisible = !get('FilterCreatorLearnMore');

    const dismissCreatorBanner = useCallback(() => {
        if (!isCreatorBannerVisible) {
            return;
        }

        perform('FilterCreatorLearnMore', true);
    }, [isCreatorBannerVisible, perform]);

    return {
        activeBpmFilters,
        activeKeyFilters,
        bpm,
        keyrootnote,
        keyquality,
        isCreatorBannerVisible,
        dismissCreatorBanner,
        onBpmClear,
        addBpmFilter,
        onKeysClear,
        toggleKeyRootFilter,
        toggleKeyQualityFilter,
    };
};
